import { ArrowBackOutlined, BlockOutlined, EditOutlined, MoreHorizOutlined, ReplayOutlined } from '@mui/icons-material';
import { Alert, Box, Button, IconButton, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { useId, useState } from 'react';
import type { GridColDef } from '@mui/x-data-grid';
import {
  AnchoredMenu,
  DefinitionList,
  DialogSurface,
  FormDialog,
  FormTextField,
  StatusBadge,
  type DefinitionListItem,
} from '../../../design-system';
import { errorCode } from '../model/resource';
import {
  apiErrorMessage,
  blockerLabels,
  referenceBlockers,
  statusLabel,
  statusTone,
  todayInSeoul,
  useOperationKey,
  validDate,
  validReason,
} from '../model/referenceData';
import type { ReferenceDataBlocker, ReferenceDataItem } from '../model/types';
export function BackToReferenceData({ onBack }: { onBack: () => void }) {
  return (
    <Tooltip title="기준 정보로 돌아가기">
      <span>
        <IconButton aria-label="기준 정보로 돌아가기" onClick={onBack}>
          <ArrowBackOutlined />
        </IconButton>
      </span>
    </Tooltip>
  );
}
type LifecycleAction = 'end' | 'reactivate';
type LifecycleChange = {
  version: number;
  effectiveDate: string;
  reason: string;
  end?: boolean;
  reactivate?: boolean;
};
function fixtureLifecycleChange<T extends ReferenceDataItem>(item: T, input: LifecycleChange): T {
  if (input.end) {
    return {
      ...item,
      status: input.effectiveDate > todayInSeoul() ? 'END_SCHEDULED' : 'ENDED',
      effectiveTo: input.effectiveDate,
      version: item.version + 1,
    };
  }
  return {
    ...item,
    status: input.effectiveDate > todayInSeoul() ? 'SCHEDULED' : 'ACTIVE',
    effectiveFrom: input.effectiveDate,
    effectiveTo: undefined,
    version: item.version + 1,
  };
}
export function useReferenceLifecycle<T extends ReferenceDataItem>({
  kindLabel,
  fixtureMode,
  change,
  fixtureBlockers = {},
  onChanged,
  onNotice,
}: {
  kindLabel: string;
  fixtureMode: boolean;
  change: (id: string, input: LifecycleChange, idempotencyKey: string) => Promise<T>;
  fixtureBlockers?: Partial<Record<string, ReferenceDataBlocker[]>>;
  onChanged: (item: T) => void;
  onNotice: (message: string) => void;
}) {
  const [target, setTarget] = useState<T>();
  const [action, setAction] = useState<LifecycleAction>('end');
  const [effectiveDate, setEffectiveDate] = useState(todayInSeoul());
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [blockers, setBlockers] = useState<ReferenceDataBlocker[]>([]);
  const operationKey = useOperationKey();
  const open = (nextAction: LifecycleAction, item: T) => {
    setTarget(item);
    setAction(nextAction);
    setEffectiveDate(todayInSeoul());
    setReason('');
    setError(undefined);
    setBlockers([]);
    operationKey.reset();
  };
  const close = () => {
    if (busy) return;
    setTarget(undefined);
    setError(undefined);
    setBlockers([]);
  };
  const submit = () => {
    if (!target || busy) return;
    if (!validDate(effectiveDate) || !validReason(reason)) {
      setError('적용일과 변경 사유를 확인해 주세요. 변경 사유는 1자 이상 500자 이하로 입력해야 합니다.');
      return;
    }
    const input: LifecycleChange = {
      version: target.version,
      effectiveDate,
      reason: reason.trim(),
      ...(action === 'end'
        ? {
            end: true,
          }
        : {
            reactivate: true,
          }),
    };
    const idempotencyKey = operationKey.forPayload(input);
    setBusy(true);
    setError(undefined);
    setBlockers([]);
    const request = fixtureMode
      ? fixtureBlockers[target.id]?.length && action === 'end'
        ? Promise.reject(
            Object.assign(new Error('reference is in use'), {
              code: 'REFERENCE_IN_USE',
              details: {
                blockers: fixtureBlockers[target.id],
              },
            }),
          )
        : Promise.resolve(fixtureLifecycleChange(target, input))
      : change(target.id, input, idempotencyKey);
    void request
      .then(
        (updated) => {
          onChanged(updated);
          onNotice(
            action === 'end'
              ? updated.status === 'END_SCHEDULED'
                ? `${updated.descriptor} 사용 종료를 예약했습니다.`
                : `${updated.descriptor} 사용을 종료했습니다.`
              : updated.status === 'SCHEDULED'
                ? `${updated.descriptor} 재사용을 예약했습니다.`
                : `${updated.descriptor}을(를) 다시 사용하도록 변경했습니다.`,
          );
          setTarget(undefined);
          operationKey.reset();
        },
        (cause: unknown) => {
          const nextBlockers = referenceBlockers(cause);
          if (errorCode(cause) === 'REFERENCE_IN_USE') {
            setError('현재 사용 중인 항목이 있어 사용을 종료할 수 없습니다. 먼저 연결된 정보를 변경해 주세요.');
            setBlockers(nextBlockers);
          } else {
            setError(apiErrorMessage(cause, `${kindLabel} 상태를 변경하지 못했습니다.`));
          }
        },
      )
      .finally(() => setBusy(false));
  };
  return {
    open,
    dialog: (
      <ReferenceLifecycleDialog
        item={target}
        action={action}
        kindLabel={kindLabel}
        effectiveDate={effectiveDate}
        reason={reason}
        busy={busy}
        error={error}
        blockers={blockers}
        onEffectiveDateChange={(value) => {
          setEffectiveDate(value);
          setError(undefined);
          setBlockers([]);
        }}
        onReasonChange={(value) => {
          setReason(value);
          setError(undefined);
          setBlockers([]);
        }}
        onClose={close}
        onSubmit={submit}
      />
    ),
  };
}
function ReferenceLifecycleDialog<T extends ReferenceDataItem>({
  item,
  action,
  kindLabel,
  effectiveDate,
  reason,
  busy,
  error,
  blockers,
  onEffectiveDateChange,
  onReasonChange,
  onClose,
  onSubmit,
}: {
  item?: T;
  action: LifecycleAction;
  kindLabel: string;
  effectiveDate: string;
  reason: string;
  busy: boolean;
  error?: string;
  blockers: ReferenceDataBlocker[];
  onEffectiveDateChange: (value: string) => void;
  onReasonChange: (value: string) => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const end = action === 'end';
  return (
    <FormDialog
      open={Boolean(item)}
      onClose={onClose}
      onSubmit={onSubmit}
      title={item ? `${item.descriptor} ${end ? '사용 종료' : '다시 사용'}` : `${kindLabel} 상태 변경`}
      cancelLabel="취소"
      submitLabel={end ? '사용 종료' : '다시 사용'}
      busy={busy}
      busyLabel="처리 중…"
      submitDisabled={!validDate(effectiveDate) || !validReason(reason)}
      submitColor={end ? 'error' : 'primary'}
      maxWidth="xs"
    >
      <Stack spacing={1.5}>
        <Alert severity={end ? 'warning' : 'info'}>
          {end
            ? '사용을 종료해도 기존 이력은 삭제되지 않습니다. 적용일부터 새로운 구성원·조직 변경에서 선택할 수 없습니다.'
            : '기존 이력은 유지하고 적용일부터 새로운 구성원·조직 변경에서 다시 선택할 수 있게 합니다.'}
        </Alert>
        {error ? (
          <Alert severity="error" role="alert">
            <Typography variant="body2">{error}</Typography>
            {blockers.length ? (
              <Box
                component="ul"
                sx={{
                  my: 0.75,
                  pl: 2.5,
                }}
              >
                {blockers.map((blocker) => (
                  <li key={blocker.type}>
                    <Typography variant="body2">
                      {blockerLabels[blocker.type] ?? blocker.type} {blocker.count}건
                    </Typography>
                  </li>
                ))}
              </Box>
            ) : null}
          </Alert>
        ) : null}
        <FormTextField
          required
          type="date"
          label={end ? '사용 종료일' : '적용일'}
          value={effectiveDate}
          disabled={busy}
          error={!validDate(effectiveDate)}
          helperText={end ? '선택한 날짜부터 사용할 수 없습니다.' : '선택한 날짜부터 다시 사용할 수 있습니다.'}
          onChange={(event) => onEffectiveDateChange(event.target.value)}
        />
        <FormTextField
          required
          multiline
          minRows={3}
          label="변경 사유"
          value={reason}
          disabled={busy}
          error={reason.length > 0 && !validReason(reason)}
          helperText="감사 이력에 남길 사유를 500자 이하로 입력해 주세요."
          onChange={(event) => onReasonChange(event.target.value)}
          slotProps={{
            htmlInput: {
              maxLength: 500,
            },
          }}
        />
      </Stack>
    </FormDialog>
  );
}
export function CatalogDetailDialog<T extends ReferenceDataItem>({
  item,
  kindLabel,
  canWrite,
  canEdit = true,
  editDisabledReason,
  extraItems = [],
  onClose,
  onEdit,
  onLifecycle,
}: {
  item?: T;
  kindLabel: string;
  canWrite: boolean;
  canEdit?: boolean;
  editDisabledReason?: string;
  extraItems?: DefinitionListItem[];
  onClose: () => void;
  onEdit: () => void;
  onLifecycle: (action: LifecycleAction) => void;
}) {
  const ended = item?.status === 'ENDED';
  const active = item?.status === 'ACTIVE';
  const actions =
    canWrite && item ? (
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        {active || ended ? (
          <Button
            color={active ? 'error' : 'primary'}
            variant="outlined"
            startIcon={active ? <BlockOutlined /> : <ReplayOutlined />}
            onClick={() => { onClose(); onLifecycle(active ? 'end' : 'reactivate'); }}
          >
            {active ? '사용 종료' : '다시 사용'}
          </Button>
        ) : null}
        {!ended && canEdit ? (
          <Button variant="contained" startIcon={<EditOutlined />} onClick={onEdit}>
            수정
          </Button>
        ) : null}
      </Stack>
    ) : undefined;
  return (
    <DialogSurface
      open={Boolean(item)}
      onClose={onClose}
      title={item?.descriptor ?? ''}
      description={`${kindLabel} 기준정보`}
      closeLabel="상세 닫기"
      size="compact"
      actions={actions}
    >
      <Stack spacing={2}>
        {item ? (
          <DefinitionList
            items={[
              ['상태', <StatusBadge key="status" label={statusLabel(item.status)} tone={statusTone(item.status)} />],
              ['참조 코드', item.code],
              ['이름', item.descriptor],
              ...extraItems,
              ['적용 시작일', item.effectiveFrom],
              ['사용 종료일', item.effectiveTo ?? '없음'],
            ]}
          />
        ) : null}
        {!canWrite ? <Alert severity="info">조회 권한만 있어 기준정보를 변경할 수 없습니다.</Alert> : null}
        {canWrite && editDisabledReason ? <Alert severity="info">{editDisabledReason}</Alert> : null}
      </Stack>
    </DialogSurface>
  );
}

export function canEditCatalogItem(item: ReferenceDataItem): boolean {
  return item.status !== 'SCHEDULED' && item.status !== 'ENDED';
}

function CatalogRowActions<T extends ReferenceDataItem>({ item, canWrite, onInspect, onEdit, onLifecycle, tabIndex }: {
  item: T;
  canWrite: boolean;
  onInspect: (item: T) => void;
  onEdit: (item: T) => void;
  onLifecycle: (action: LifecycleAction, item: T) => void;
  tabIndex: number;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const run = (action: () => void) => { setAnchor(null); action(); };
  return <Box onClick={(event) => event.stopPropagation()}>
    <IconButton size="small" tabIndex={tabIndex} aria-label={`${item.descriptor} 작업`} aria-haspopup="menu" aria-controls={anchor ? menuId : undefined} aria-expanded={Boolean(anchor)}
      onClick={(event) => { event.stopPropagation(); setAnchor(event.currentTarget); }}>
      <MoreHorizOutlined fontSize="small" />
    </IconButton>
    <AnchoredMenu id={menuId} anchorEl={anchor} onClose={() => setAnchor(null)} ariaLabel={`${item.descriptor} 작업`} placement="bottom-end">
      <MenuItem onClick={() => run(() => onInspect(item))}>상세 정보</MenuItem>
      {canWrite && canEditCatalogItem(item) && <MenuItem onClick={() => run(() => onEdit(item))}>수정</MenuItem>}
      {canWrite && item.status === 'ACTIVE' && <MenuItem onClick={() => run(() => onLifecycle('end', item))}>사용 종료</MenuItem>}
      {canWrite && item.status === 'ENDED' && <MenuItem onClick={() => run(() => onLifecycle('reactivate', item))}>다시 사용</MenuItem>}
    </AnchoredMenu>
  </Box>;
}

export function catalogActionsColumn<T extends ReferenceDataItem>(options: {
  canWrite: boolean;
  onInspect: (item: T) => void;
  onEdit: (item: T) => void;
  onLifecycle: (action: LifecycleAction, item: T) => void;
}): GridColDef<T> {
  return { field: 'actions', headerName: '', width: 64, sortable: false, filterable: false, disableColumnMenu: true,
    renderCell: ({ row, tabIndex }) => <CatalogRowActions item={row} tabIndex={tabIndex} {...options} />,
  };
}
